import { Link, useParams } from "react-router-dom";
import { Clock, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTAPair } from "../components/CTA";
import { Reveal } from "../components/Motion";
import { getCity } from "../data/cities";
import { getService } from "../data/services";
import NotFound from "./NotFound";

const MatrixDetail = () => {
  const { citySlug, serviceSlug } = useParams();
  const city = getCity(citySlug);
  const service = getService(serviceSlug);
  if (!city || !service) return <NotFound />;
  const shortName = service.name.replace(" Repair", "").replace(" Programs", "");

  return (
    <main data-testid="matrix-detail-page">
      <SEO
        title={`${service.name} in ${city.name}, TX | Mobile Welder On-Site`}
        description={`${service.name} in ${city.name}, TX — on-site mobile welding, on-site ${city.response}. Written price before work starts. Text a photo for a fast quote.`}
      />
      <section className="bg-paper grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-14 md:pt-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-steel font-semibold mb-4">
                {city.name}, TX · {city.county} County
              </p>
              <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="matrix-title">
                {service.name} in {city.name}
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-smoke leading-relaxed" data-testid="matrix-intro">
                Need {shortName.toLowerCase()} work in {city.name}? {city.blurb} For this job that means: text us two photos, get a written flat price, and we weld it on-site — usually in a single visit.
              </p>
              <div className="mt-8"><CTAPair service={`${service.name} in ${city.name}`} testidPrefix="matrix-cta" /></div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-rust pointer-events-none" aria-hidden />
              <img src={service.image} alt={`${service.name} in ${city.name}, TX`} data-testid="matrix-image" className="relative w-full aspect-[4/3] object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream border-y border-warmline py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12">
          <Reveal>
            <div>
              <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl">What we fix in {city.name}</h2>
              <ul className="mt-6 space-y-3.5">
                {service.whoNeeds.map((w) => (
                  <li key={w} className="flex gap-3 text-smoke leading-relaxed">
                    <CheckCircle2 size={20} className="text-rust shrink-0 mt-0.5" aria-hidden />{w}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <div className="flex items-center gap-3 bg-paper border border-warmline p-5 mb-6" data-testid="matrix-response">
                <Clock size={22} className="text-rust shrink-0" aria-hidden />
                <p className="text-[15px] text-smoke"><strong className="text-ink">Response in {city.name}:</strong> on-site {city.response}. Same-day slots most weeks.</p>
              </div>
              <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl">Materials handled</h2>
              <ul className="mt-5 space-y-3">
                {service.materials.map((m) => (
                  <li key={m} className="flex gap-3 text-smoke leading-relaxed">
                    <span className="w-2 h-2 bg-steel rotate-45 shrink-0 mt-2.5" aria-hidden />{m}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="cut-top bg-charcoal text-cream py-16 -mt-[26px] relative z-[5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-4xl" data-testid="matrix-cta-heading">
              {city.name} repairs start with two photos
            </h2>
            <p className="mt-2 text-cream/70">Written price before work starts · Workmanship warranty · No repair charge if it can't be fixed</p>
          </div>
          <CTAPair service={`${service.name} in ${city.name}`} dark testidPrefix="matrix-band-cta" />
        </div>
      </section>

      <section className="bg-paper py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap gap-x-8 gap-y-3 text-sm font-semibold">
          <Link to={`/service-areas/${city.slug}`} data-testid="matrix-city-link" className="text-rust hover:text-rust-dark transition-colors">All repairs in {city.name}</Link>
          <Link to={`/services/${service.slug}`} data-testid="matrix-service-link" className="text-rust hover:text-rust-dark transition-colors">About our {service.name.toLowerCase()}</Link>
          <Link to="/services" data-testid="matrix-all-services" className="text-smoke hover:text-ink transition-colors">All services</Link>
        </div>
      </section>
    </main>
  );
};

export default MatrixDetail;
